import React, { useState, useEffect } from 'react';
import { Card, Alert, Button } from '../components/ui';
import PostModal from '../components/PostModal/PostModal';
import AnalyticsSection from '../components/AnalyticsSection';
import SuccessOverlay from '../components/SuccessOverlay';
import { Users, Image, Calendar } from 'lucide-react';
import { getRedirectUrl } from '../utils/helpers';
import { useDashboard } from '../hooks/useDashboard';
import { formatDateHelper } from '../utils/dateHelpers';
import { PageSkeleton, PostCardSkeleton } from '../components/Skeletons/dashboard';
import { useOnboardingStore } from '../store/useOnboardingStore';
import { usePostModalStore } from '../store/usePostModalStore';
import VideoPlayer from '../components/VideoPlayer';

const DashboardPage = () => {
  const {
    // Data
    profile,
    displayPosts,
    socialAccounts,
    stats,
    showUpgradeCTA,
    // Loading and error states
    isLoading,
    error,
  } = useDashboard();

  const { openModal } = usePostModalStore();

  const handlePostClick = (post) => {
    openModal(post);
  };

  const { firstTimeLogin, setFirstTimeLogin } = useOnboardingStore();
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const redirectUri = getRedirectUrl('/dashboard/subscription');

  // Show success overlay on first time login
  useEffect(() => {
    if (firstTimeLogin && !isLoading) {
      setShowSuccessOverlay(true);
    }
  }, [firstTimeLogin, isLoading]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (error) {
    return <Alert variant='error'>Failed to load dashboard data: {error.message}</Alert>;
  }

  return (
    <div className='space-y-6'>
      {/* Personalized Dashboard Header */}
      <div className='bg-gradient-to-r from-slate-100 via-slate-50 to-slate-200 rounded-xl p-6 text-slate-800 relative overflow-hidden border border-slate-200'>
        {/* Background Pattern */}
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32'></div>
          <div className='absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24'></div>
        </div>

        <div className='relative'>
          {/* Header with Logo and Title */}
          <div className='flex items-start gap-4 mb-4'>
            {/* Business Logo */}
            <div className='w-16 h-16 md:w-20 md:h-20 bg-black rounded-2xl flex items-center justify-center border border-slate-200 shadow-lg flex-shrink-0'>
              {profile?.logo ? (
                <img
                  src={profile.logo}
                  alt='Business Logo'
                  className='w-12 h-12 md:w-16 md:h-16 rounded-xl object-contain'
                />
              ) : (
                <div className='w-12 h-12 md:w-16 md:h-16 bg-slate-200 rounded-xl flex items-center justify-center'>
                  <span className='text-2xl md:text-3xl font-bold text-slate-600'>
                    {profile?.business_name ? profile.business_name.charAt(0).toUpperCase() : 'B'}
                  </span>
                </div>
              )}
            </div>

            {/* Business Title and Subtitle */}
            <div className='flex-1 min-w-0'>
              <h1 className='text-2xl md:text-3xl font-bold mb-1'>
                {profile?.business_name || 'Your Business'}
              </h1>
              <p className='text-slate-600 text-sm md:text-base'>
                Welcome back, this is a preview version! Here's what's happening with your content.
              </p>
            </div>
          </div>

          {/* Description - Full Width */}
          {profile?.description && (
            <p className='text-slate-500 text-sm leading-relaxed mb-3'>{profile.description}</p>
          )}

          {/* Tags - Full Width */}
          {(profile?.industry || profile?.city) && (
            <div className='flex items-center flex-wrap gap-2'>
              {profile?.industry && (
                <span className='bg-slate-200 px-3 py-1 rounded-full text-xs font-medium text-slate-700'>
                  🏢 {profile.industry.charAt(0).toUpperCase() + profile.industry.slice(1)}
                </span>
              )}
              {profile?.city && (
                <span className='bg-slate-200 px-3 py-1 rounded-full text-xs font-medium text-slate-700'>
                  📍 {profile.city}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Posts - Horizontal List */}
      <Card>
        <Card.Body>
          {displayPosts.length === 0 ? (
            <div className='text-center py-8'>
              <Calendar className='w-12 h-12 text-gray-300 mx-auto mb-3' />
              <p className='text-gray-500'>No upcoming posts scheduled</p>
              <p className='text-sm text-gray-400 mt-1'>Visit the Calendar to schedule content</p>
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              {displayPosts.map((post) => {
                if (post.status === 'regeneration' || post.status === 'draft') {
                  return (
                    <div
                      key={post.id}
                      className='relative pt-8 flex-1 flex-col justify-center justify-items-center items-center'
                    >
                      <div className='relative flex w-[235px]'>
                        <VideoPlayer key={post.id} overlayText='Regenerating post...' />
                      </div>
                    </div>
                  );
                }
                // Show skeleton loader for draft posts
                if (post.status === 'draft') {
                  return <PostCardSkeleton key={post.id} />;
                }

                // Determine CSS classes based on status and type
                let cardClasses =
                  'w-full p-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer';

                // Status-based styling
                if (post.status === 'approved') {
                  cardClasses += ' bg-blue-50 border-2 border-blue-200';
                } else if (post.status === 'published') {
                  cardClasses += ' bg-green-50 border-2 border-green-200';
                } else if (
                  post.status === 'pending' ||
                  !post.socialPlatforms ||
                  post.socialPlatforms.length === 0
                ) {
                  cardClasses += ' bg-white border-2 border-dashed border-gray-300';
                } else {
                  cardClasses += ' bg-gray-50 border border-gray-200';
                }

                return (
                  <div key={post.id} className='flex flex-col'>
                    {/* Date and Time - Outside card */}
                    <div className='mb-2 px-1 flex items-center justify-between'>
                      <p className='text-sm font-semibold text-gray-900'>
                        {formatDateHelper(post.scheduledAt, 'hh:mm a  MMM d')}
                      </p>
                      {/* Type badge */}
                      {post.aspectRatio === '9:16' ? (
                        <span className='bg-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-bold'>
                          Story
                        </span>
                      ) : (
                        <span className='bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-bold'>
                          Feed
                        </span>
                      )}
                    </div>

                    {/* Card with Image */}
                    <div onClick={() => handlePostClick(post)} className={cardClasses}>
                      {post.aspectRatio === '9:16' ? (
                        // Story posts - full height image
                        <div className='w-full h-80 bg-gray-100 rounded-lg overflow-hidden shadow-sm relative flex items-center justify-center'>
                          {post.path || post.imageUrl ? (
                            <img
                              src={post.path || post.imageUrl}
                              alt='Post'
                              className='w-auto h-full object-contain'
                            />
                          ) : (
                            <div className='w-full h-full flex items-center justify-center'>
                              <Image className='w-12 h-12 text-gray-400' />
                            </div>
                          )}
                        </div>
                      ) : (
                        // Feed posts - image + caption
                        <div className='w-full h-80 bg-white rounded-lg overflow-hidden shadow-sm relative flex flex-col'>
                          {/* Image */}
                          <div className='flex-shrink-0 bg-gray-100 flex items-center justify-center'>
                            {post.path || post.imageUrl ? (
                              <img
                                src={post.path || post.imageUrl}
                                alt='Post'
                                className='w-full h-auto object-contain'
                              />
                            ) : (
                              <div className='w-full h-48 flex items-center justify-center'>
                                <Image className='w-12 h-12 text-gray-400' />
                              </div>
                            )}
                          </div>

                          {/* Caption */}
                          {post.caption && (
                            <div className='hidden md:block px-3 pt-2 pb-1 bg-white overflow-hidden'>
                              <p className='text-sm text-gray-800 leading-snug line-clamp-4'>
                                {post.caption}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Upgrade CTA Card for Welcome plan */}
              {showUpgradeCTA && (
                <div className='flex flex-col'>
                  {/* Spacer to align with date/time */}
                  <div className='mb-2 px-1'>
                    <div className='h-5'></div>
                  </div>
                  {/* CTA Card */}
                  <div className='w-full p-4 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg hover:shadow-lg transition-all cursor-pointer'>
                    <div className='h-80 flex flex-col items-center justify-center space-y-4 rounded-lg'>
                      <div className='text-5xl'>🚀</div>
                      <h3 className='text-lg font-bold text-gray-900 text-center'>
                        Unlock More Posts
                      </h3>
                      <p className='text-xs text-gray-600 text-center px-2'>
                        Upgrade to see all your scheduled content
                      </p>
                      <Button
                        variant='primary'
                        onClick={() => {
                          window.location.href = redirectUri;
                        }}
                        className='w-full'
                      >
                        Upgrade Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Stats & Connected Accounts Row */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Stats Cards */}
        <div className='grid grid-cols-2 gap-4'>
          <Card>
            <Card.Body>
              <div className='flex items-center'>
                <div className='w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center'>
                  <Image className='w-6 h-6 text-blue-600' />
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-600'>Total Posts</p>
                  <p className='text-2xl font-bold text-gray-900'>{stats.totalPosts}</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className='flex items-center'>
                <div className='w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center'>
                  <Calendar className='w-6 h-6 text-green-600' />
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-600'>Scheduled</p>
                  <p className='text-2xl font-bold text-gray-900'>{stats.scheduledPosts}</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className='col-span-2'>
            <Card.Body>
              <div className='flex items-center'>
                <div className='w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center'>
                  <Users className='w-6 h-6 text-orange-600' />
                </div>
                <div className='ml-4'>
                  <p className='text-sm font-medium text-gray-600'>Connected Accounts</p>
                  <p className='text-2xl font-bold text-gray-900'>{stats.connectedAccounts}</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Connected Accounts */}
        <Card>
          <Card.Body>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-lg font-semibold text-gray-900'>Connected Accounts</h2>
              <Users className='w-5 h-5 text-gray-400' />
            </div>

            {socialAccounts?.length === 0 ? (
              <div className='text-center py-8'>
                <Users className='w-12 h-12 text-gray-300 mx-auto mb-3' />
                <p className='text-gray-500'>No social accounts connected</p>
                <p className='text-sm text-gray-400 mt-1'>Connect your accounts to start posting</p>
              </div>
            ) : (
              <div className='space-y-3'>
                {socialAccounts.length > 0 &&
                  socialAccounts?.map((account) => (
                    <div
                      key={account.id}
                      className='flex items-center space-x-3 p-3 bg-gray-50 rounded-lg'
                    >
                      <div className='w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center'>
                        {account.profile_photo ? (
                          <img
                            src={account.profile_photo}
                            alt='Profile'
                            className='w-full h-full object-cover'
                          />
                        ) : (
                          <Users className='w-5 h-5 text-gray-400' />
                        )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-gray-900 truncate'>
                          {account.username}
                        </p>
                        <p className='text-xs text-gray-500 capitalize'>
                          {account.platform} • {account.account_type}
                        </p>
                      </div>
                      <div className='flex items-center'>
                        <span
                          className={`w-2 h-2 rounded-full ${
                            account.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Post Details Modal */}
      <PostModal />

      {/* Success Overlay for first time login */}
      {showSuccessOverlay && (
        <SuccessOverlay
          message='Welcome to Your Dashboard! 🎉'
          onComplete={() => {
            setFirstTimeLogin(false);
          }}
        />
      )}
    </div>
  );
};

export default DashboardPage;
